/**
 * Slice Redux pour l'état de l'interface utilisateur
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UIState {
  sidebarOpen: boolean;
  theme: 'light' | 'dark';
  density: 'comfortable' | 'compact';
  // Accessibilité tableaux — utile en délibération (jury, personnes âgées)
  tableBorders: boolean;       // affiche les lignes verticales
  tableFontScale: number;      // 0.8 → 1.6 (1 = taille par défaut)
  notifications: Notification[];
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

const _readFontScale = (): number => {
  const raw = localStorage.getItem('tableFontScale');
  if (!raw) return 1;
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return 1;
  return Math.min(1.6, Math.max(0.8, n));
};

const initialState: UIState = {
  sidebarOpen: true,
  theme: (localStorage.getItem('theme') as 'light' | 'dark') || 'light',
  density: (localStorage.getItem('density') as 'comfortable' | 'compact') || 'comfortable',
  tableBorders: localStorage.getItem('tableBorders') === 'true',
  tableFontScale: _readFontScale(),
  notifications: [],
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', state.theme);
    },
    toggleDensity: (state) => {
      state.density = state.density === 'comfortable' ? 'compact' : 'comfortable';
      localStorage.setItem('density', state.density);
    },
    toggleTableBorders: (state) => {
      state.tableBorders = !state.tableBorders;
      localStorage.setItem('tableBorders', state.tableBorders ? 'true' : 'false');
    },
    setTableFontScale: (state, action: PayloadAction<number>) => {
      const v = Math.min(1.6, Math.max(0.8, action.payload));
      state.tableFontScale = Math.round(v * 100) / 100;
      localStorage.setItem('tableFontScale', String(state.tableFontScale));
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString(),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  toggleTheme,
  toggleDensity,
  toggleTableBorders,
  setTableFontScale,
  addNotification,
  removeNotification,
  clearNotifications,
} = uiSlice.actions;

export default uiSlice.reducer;
